import type { Vec2 } from "kaplay";

// First-person shotgun: ammo state, firing, reloading, muzzle flash + recoil,
// and the perspective look/animation. Audio is wired in Phase 11.
//
// - Left click -> tryFire(): consumes a shell, muzzle flash + recoil. Empty =
//   dry fire (no shot). Locked while reloading.
// - Right click -> startReload(): rolls the gun to the side and refills to full
//   after reload_time, firing locked throughout.
export type FireResult = "fired" | "empty" | "locked";

export interface Gun {
  tryFire(): FireResult;
  startReload(): boolean; // true if a reload actually started
  isReloading(): boolean;
  shells(): number;
  magazineSize(): number;
  reloadProgress(): number; // 0..1 (1 when not reloading)
}

interface WeaponCfg {
  magazine_size: number;
  reload_time: number;
}

function starPts(spikes: number, outer: number, inner: number): Vec2[] {
  const pts: Vec2[] = [];
  for (let i = 0; i < spikes * 2; i++) {
    const r = i % 2 ? inner : outer;
    const a = (i / (spikes * 2)) * Math.PI * 2 - Math.PI / 2;
    pts.push(vec2(Math.cos(a) * r, Math.sin(a) * r));
  }
  return pts;
}

const GUN_SCALE = 0.284; // display scale of the (half-res) art sprite
const MUZZLE_OFF = 394; // sprite px from the cropped bottom (anchor) up to the muzzle

// Reload pump cycle (gun1 -> gun0 -> racked -> gun0) + each frame's downward chunk.
const RELOAD_SEQ = ["gun1", "gun", "gun_reload", "gun"];
const CHUNK: Record<string, number> = { gun1: 5, gun: 0, gun_reload: 12 };

export function addGun(weapon: WeaponCfg, getAim: () => Vec2): Gun {
  const baseX = width() / 2;
  const baseY = height() + 8; // anchor "bot" sits the grip just off the bottom
  const mag = Math.max(1, Math.floor(weapon.magazine_size));

  let shells = mag;
  let reloadLeft = 0;
  let reloadDur = 0;
  let recoil = 0; // 1 on fire, decays to 0

  const gun = add([
    sprite("gun"),
    pos(baseX, baseY),
    anchor("bot"),
    rotate(0),
    scale(GUN_SCALE),
    z(900),
  ]);

  // Swap the gun frame only when it changes (reload pump alternates two frames).
  let curFrame = "gun";
  function setFrame(name: string) {
    if (curFrame !== name) {
      curFrame = name;
      gun.use(sprite(name));
    }
  }

  // World position of the muzzle tip, following the gun's bob/sway/angle.
  function muzzleWorld(): Vec2 {
    const rad = (gun.angle * Math.PI) / 180;
    const len = MUZZLE_OFF * GUN_SCALE;
    return gun.pos.add(vec2(Math.sin(rad) * len, -Math.cos(rad) * len));
  }

  function spawnMuzzleFlash() {
    const flash = add([pos(muzzleWorld()), rotate(rand(0, 360)), z(950)]);
    const max = 0.07;
    let life = max;
    flash.onUpdate(() => {
      life -= dt();
      if (life <= 0) destroy(flash);
    });
    flash.onDraw(() => {
      const op = Math.max(0, life / max);
      drawPolygon({ pts: starPts(5, 26, 10), color: rgb(255, 138, 30), opacity: op * 0.9 });
      drawCircle({ radius: 13, color: rgb(255, 210, 74), opacity: op });
      drawCircle({ radius: 7, color: rgb(255, 246, 207), opacity: op });
    });
  }

  gun.onUpdate(() => {
    recoil = Math.max(0, recoil - dt() * 6); // ~0.16s recovery

    if (reloadLeft > 0) {
      reloadLeft = Math.max(0, reloadLeft - dt());
      if (reloadLeft === 0) shells = mag; // refill when the reload completes
      // pump action: cycle the 3 frames (gun1 -> gun0 -> racked -> gun0) with a
      // small downward "chunk" per frame, while the gun rolls to the side (eased
      // in/out over the reload).
      const t = 1 - reloadLeft / reloadDur;
      const e = Math.sin(t * Math.PI); // 0 -> 1 -> 0
      const elapsed = reloadDur - reloadLeft;
      const fname = RELOAD_SEQ[Math.floor(elapsed / 0.13) % RELOAD_SEQ.length];
      setFrame(fname);
      gun.angle = -24 * e; // tilt to the side while reloading
      gun.pos.x = baseX - 16 * e;
      gun.pos.y = baseY + (CHUNK[fname] ?? 0) + 6 * e;
    } else {
      setFrame("gun");
      const sway = Math.max(-14, Math.min(14, (getAim().x - baseX) * 0.04));
      gun.angle = sway * 0.12;
      gun.pos.x = baseX + sway;
      gun.pos.y = baseY + Math.sin(time() * 2.2) * 3 + recoil * 18; // recoil kick
    }
  });

  return {
    tryFire() {
      if (reloadLeft > 0) return "locked";
      if (shells <= 0) return "empty";
      shells--;
      recoil = 1;
      spawnMuzzleFlash();
      return "fired";
    },
    startReload() {
      if (reloadLeft > 0 || shells >= mag) return false;
      // reload time scales with shells to load: empty (mag) = full reload_time,
      // 1 shell to load = 1/mag of it. The animation follows reloadDur, so it
      // shortens automatically.
      reloadDur = Math.max(0.1, weapon.reload_time * ((mag - shells) / mag));
      reloadLeft = reloadDur;
      return true;
    },
    isReloading() {
      return reloadLeft > 0;
    },
    shells() {
      return shells;
    },
    magazineSize() {
      return mag;
    },
    reloadProgress() {
      return reloadLeft > 0 ? 1 - reloadLeft / reloadDur : 1;
    },
  };
}
